import {
  HydrationBoundary,
  QueryClient,
  dehydrate,
} from "@tanstack/react-query";
import {
  Link,
  data,
  isRouteErrorResponse,
  redirect,
  useLoaderData,
  useRouteError,
  type HeadersFunction,
  type LoaderFunctionArgs,
  type MetaFunction,
  type ShouldRevalidateFunction,
} from "react-router";
import TopHalalStockInfo from "../UI/pages/TopHalalStock/components/TopHalalStockInfo/TopHalalStockInfo";
import { financialIntegrationKeys } from "../api/services/financial-integration.hooks";
import { fixedT } from "../i18n/i18n.server";
import { splitLocalePath, withLocale } from "../i18n/localePath";
import { PathGenerators, Paths } from "../router/paths";
import { publicShariahKey, relatedStocksKey } from "../seo/publicStockFields";
import { localeAlternateMeta, seoTagsToMeta, type SeoTags } from "../seo/routeMeta";
import { buildStockSeoTags } from "../seo/stockSeo";
import { routeBreadcrumb } from "../seo/structuredData";
import { serverConfig } from "../server/config.server";
import { fetchRelatedHalalStocks } from "../server/screenerData.server";
import {
  StockNotFoundError,
  fetchFinancialAnalytics,
  fetchPublicShariahSummary,
} from "../server/stockData.server";

/** Seconds a page rendered without backend data may be cached. */
const DEGRADED_TTL_SECONDS = 60;

const cacheControl = (ttl: number) =>
  `public, max-age=0, s-maxage=${ttl}, stale-while-revalidate=${ttl}`;

export async function loader({ params, request }: LoaderFunctionArgs) {
  const raw = params.symbol ?? "";
  const ticker = raw.toUpperCase();

  const url = new URL(request.url);
  const locale = splitLocalePath(url.pathname).locale;

  // Tickers are canonically uppercase: /symbol/avgo -> /symbol/AVGO.
  if (raw !== ticker) {
    const target = PathGenerators.SYMBOL(ticker);
    throw redirect(`${locale ? withLocale(target, locale) : target}${url.search}`, 301);
  }

  const [analyticsResult, shariah] = await Promise.all([
    fetchFinancialAnalytics(ticker).then(
      (value) => ({ ok: true as const, value }),
      (error: unknown) => ({ ok: false as const, error }),
    ),
    fetchPublicShariahSummary(ticker),
  ]);

  if (!analyticsResult.ok && analyticsResult.error instanceof StockNotFoundError) {
    throw data(
      { ticker },
      {
        status: 404,
        headers: { "Cache-Control": cacheControl(serverConfig.pageCacheTtlSeconds) },
      },
    );
  }

  // Seed TanStack Query with the server data, under the same keys the page's
  // hooks use, so the HTML has it and the browser doesn't refetch to render.
  const queryClient = new QueryClient();
  const analytics = analyticsResult.ok ? analyticsResult.value : null;
  if (analytics) {
    queryClient.setQueryData(
      financialIntegrationKeys.financialAnalytics({ symbol: ticker }),
      analytics,
    );
  }
  if (shariah) queryClient.setQueryData(publicShariahKey(ticker), shariah);

  // "Other halal stocks in <sector>": internal links between stock pages.
  const profile = analytics?.data?.companyProfile;
  const related = analytics
    ? await fetchRelatedHalalStocks(ticker, profile?.sector?.trim(), profile?.industry?.trim())
    : [];
  queryClient.setQueryData(relatedStocksKey(ticker), related);

  // Titles and descriptions in the URL's language.
  const localeT = fixedT(locale);
  const seo: SeoTags = buildStockSeoTags(
    ticker,
    analytics?.data,
    (key, options) => localeT(key, options),
    locale,
  );

  // Home → Stock Screener → "Apple Inc. (AAPL)", only for indexable pages.
  const companyName = analytics?.data?.companyProfile?.companyName?.trim();
  const symbolPath = PathGenerators.SYMBOL(ticker);
  const breadcrumb =
    seo.robots === "index, follow"
      ? routeBreadcrumb(
          "SYMBOL",
          locale ? withLocale(symbolPath, locale) : symbolPath,
          companyName ? `${companyName} (${ticker})` : ticker,
        )
      : null;

  return data(
    { ticker, seo, breadcrumb, dehydrated: dehydrate(queryClient) },
    {
      headers: {
        // Without backend data the page loads it in the browser; don't keep
        // that degraded version for long.
        "Cache-Control": cacheControl(
          analytics ? serverConfig.pageCacheTtlSeconds : DEGRADED_TTL_SECONDS,
        ),
      },
    },
  );
}

export const headers: HeadersFunction = ({ loaderHeaders, errorHeaders }) =>
  errorHeaders ?? loaderHeaders;

export const meta: MetaFunction<typeof loader> = ({ data: loaderData, error, params }) => {
  if (error || !loaderData) {
    const ticker = (params.symbol ?? "").toUpperCase();
    return [
      { title: `${ticker} not found | Akinda` },
      { name: "robots", content: "noindex, follow" },
    ];
  }
  return [
    ...seoTagsToMeta(loaderData.seo),
    ...(loaderData.seo.robots === "index, follow"
      ? localeAlternateMeta(loaderData.seo.canonical)
      : []),
    ...(loaderData.breadcrumb ? [{ "script:ld+json": loaderData.breadcrumb }] : []),
  ];
};

// Switching tabs (?tab=) must not refetch the page data from the server.
export const shouldRevalidate: ShouldRevalidateFunction = ({
  currentParams,
  nextParams,
  defaultShouldRevalidate,
}) =>
  currentParams.symbol !== nextParams.symbol ? defaultShouldRevalidate : false;

export default function SymbolRoute() {
  const { dehydrated } = useLoaderData<typeof loader>();
  return (
    <HydrationBoundary state={dehydrated}>
      <TopHalalStockInfo />
    </HydrationBoundary>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();
  const notFound = isRouteErrorResponse(error) && error.status === 404;
  const ticker = isRouteErrorResponse(error)
    ? (error.data as { ticker?: string } | undefined)?.ticker
    : undefined;

  return (
    <main style={{ padding: "64px 24px", textAlign: "center" }}>
      <h1>
        {notFound
          ? `We couldn't find ${ticker ? `"${ticker}"` : "that stock"}`
          : "Something went wrong"}
      </h1>
      <p style={{ marginTop: 12 }}>
        {notFound
          ? "Check the ticker, or search the screener for halal stocks."
          : "Please try again in a moment."}
      </p>
      <p style={{ marginTop: 24 }}>
        <Link to={Paths.STOCK_SCREENER}>Open the halal stock screener</Link>
      </p>
    </main>
  );
}
